import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/auth';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Alert,
  Paper,
  CircularProgress
} from '@mui/material';

const Setup2FA = () => {
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [secret, setSecret] = useState('');

  useEffect(() => {
    const setup = async () => {
      try {
        setLoading(true);
        const response = await authService.setup2FA();
        setQrCode(response.qrCode);
        setSecret(response.secret);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    setup();
  }, []);

  const handleVerification = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.verify2FA(verificationCode);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !qrCode) {
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5">
            Required: Set Up Two-Factor Authentication
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 3, width: '100%', textAlign: 'center' }}>
            <Typography variant="body1" gutterBottom>
              Two-factor authentication is required for enhanced security. Please follow these steps:
            </Typography>
            <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
              1. Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, etc.):
            </Typography>
            
            {qrCode && (
              <Box
                component="img"
                src={qrCode}
                alt="2FA QR Code"
                sx={{ mt: 2, mb: 2 }}
              />
            )}

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Or manually enter this code in your authenticator app:
            </Typography>
            
            <Typography
              variant="body2"
              sx={{
                fontFamily: 'monospace',
                bgcolor: 'grey.100',
                p: 1,
                borderRadius: 1,
                mb: 3
              }}
            >
              {secret}
            </Typography>

            <Typography variant="body1" gutterBottom>
              2. Enter the verification code from your authenticator app:
            </Typography>

            <Box component="form" onSubmit={handleVerification} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="verificationCode"
                label="Verification Code"
                name="verificationCode"
                autoComplete="one-time-code"
                autoFocus
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Complete 2FA Setup'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Setup2FA;
